import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  FloatingLabel,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { DomainOptionType } from "../../../constants";
import { UserDocument, useUserQuery } from "../../../graphql/menu.hooks";
import {
  ProgectsDomainByIdQuery,
  useDomainEditMutation,
} from "../../../graphql/projects.hooks";
import { useDomains } from "../../../hooks/useDomains";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import Subdomain from "../../newProject/domainSetup/subdomain";
import UserDomainView from "../../newProject/domainSetup/userDomainView";
import { isSavedChanges } from "../../../graphql/state";
import { DomainInfoToast } from "../../../components/inputs/toast/domainInfoToast/DomainInfoToast";

interface IEditProjectInfoProps {
  data: ProgectsDomainByIdQuery;
}

export default function EditProjectInfo({ data }: IEditProjectInfoProps) {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const { projectId } = useParams();

  const [title, setTitle] = useState("");
  const {
    selectedOption,
    setSelectedOption,
    setIsNextStepDisabled,
    setIsCustomStep,
    stepView,
    setStepView,
    urlType,
    domainStatusData,
    domainStatusDataLoading,
    refetchDomainStatusData,
  } = useDomains({ id: projectId });
  const { data: userData } = useUserQuery();

  const [selectedUrlType, setSelectedUrlType] = useState<number>(urlType || 0);

  const isDisableSubdomain = userData?.user?.admin
    ? false
    : !userData?.user?.activePremium;

  useEffect(() => {
    if (data?.domain?.title) {
      setTitle(data?.domain.title);
    }
  }, [data]);

  useEffect(() => {
    switch (selectedOption) {
      case DomainOptionType.Domain:
        setSelectedUrlType(2);
        break;

      case DomainOptionType.Subdomain:
        setSelectedUrlType(5);
        break;

      case DomainOptionType.UserDomain:
        setSelectedUrlType(4);
        break;

      case DomainOptionType.UserSubdomain:
        setSelectedUrlType(3);
        break;
    }
  }, [selectedOption]);

  const [domainEdit, { loading: loadingEditDomain }] = useDomainEditMutation({
    variables: {
      domainId: Number(projectId),
      title,
      urlType: selectedUrlType,
    },
    refetchQueries: [{ query: UserDocument }],
    awaitRefetchQueries: true,
    onCompleted: () => isSavedChanges(true),
  });

  const handleAddProject = useCallback(async () => {
    domainEdit();
  }, [domainEdit]);

  const checkDomainTypeWithPremium = (type: DomainOptionType): boolean => {
    return [
      DomainOptionType.Subdomain,
      DomainOptionType.UserDomain,
      DomainOptionType.UserSubdomain,
    ].includes(type);
  };

  useEffect(() => {
    if (isDisableSubdomain && checkDomainTypeWithPremium(selectedOption)) {
      setSelectedOption(DomainOptionType.Domain);
      handleAddProject();
    }
  }, [
    userData,
    selectedOption,
    isDisableSubdomain,
    handleAddProject,
    setSelectedOption,
  ]);

  const options = useMemo(
    () => [
      {
        value: DomainOptionType.Domain,
        label: (
          <>
            <p className="fs-6 mb-1">
              {t("projects.Use")} <span className="fw-bold">me-page.com</span>{" "}
              {t("projects.domain")}
            </p>
            <p className="fs-7 text-gray mb-2">
              {t(
                "projects.Your project will be hosted under our main domain (e.g. admin.me-page.com/yourproject)."
              )}
            </p>
          </>
        ),
      },
      // {
      //   value: DomainOptionType.Subdomain,
      //   label: (
      //     <>
      //       <div className="d-flex flex-column flex-md-row mb-1 align-items-baseline">
      //         <p className={`fs-6 m-0 ${isDisableSubdomain && "text-light"}`}>
      //           {t("projects.Use")}{" "}
      //           <span className="fw-bold">
      //             me-page.com {t("projects.subdomain")}
      //           </span>{" "}
      //         </p>
      //         <DomainInfoToast
      //           text={t("projects.You can activate “me-page.com subdomain” on")}
      //           isActive={isDisableSubdomain}
      //         />
      //       </div>
      //       <p
      //         className={`fs-7 mb-2 ${
      //           isDisableSubdomain ? "text-light" : "text-gray"
      //         }`}
      //       >
      //         {t(
      //           "projects.Get a custom subdomain like yourname.me-page.com (no setup required)."
      //         )}
      //       </p>
      //     </>
      //   ),
      // },
      {
        value: DomainOptionType.UserDomain,
        label: (
          <>
            <div className="d-flex flex-column flex-md-row mb-1 align-items-baseline">
              <p className={`fs-6 m-0 ${isDisableSubdomain && "text-light"}`}>
                {t("projects.Use")}{" "}
                <span className="fw-bold">{t("projects.my domain")}</span>{" "}
              </p>
              <DomainInfoToast
                text={t("projects.You can activate “My domain” on")}
                isActive={isDisableSubdomain}
              />
            </div>
            <p
              className={`fs-7 mb-2 ${
                isDisableSubdomain ? "text-light" : "text-gray"
              }`}
            >
              {t(
                "projects.Connect your own domain like yourbrand.com (requires DNS setup)."
              )}
            </p>
          </>
        ),
      },
      {
        value: DomainOptionType.UserSubdomain,
        label: (
          <>
            <div className="d-flex flex-column flex-md-row mb-1 align-items-baseline">
              <p className={`fs-6 m-0 ${isDisableSubdomain && "text-light"}`}>
                {t("projects.Use")}{" "}
                <span className="fw-bold">{t("projects.my subdomain")}</span>{" "}
              </p>
              <DomainInfoToast
                text={t("projects.You can activate “My subdomain” on")}
                isActive={isDisableSubdomain}
              />
            </div>
            <p
              className={`fs-7 mb-2 ${
                isDisableSubdomain ? "text-light" : "text-gray"
              }`}
            >
              {t(
                "projects.Use a subdomain of your own domain (e.g. app.yourbrand.com)."
              )}
            </p>
          </>
        ),
      },
    ],
    [t, isDisableSubdomain]
  );

  const handleNextStepDisabled = (value: boolean) => {
    setIsNextStepDisabled(value);
  };

  const showCustomSecondStep = (value: boolean) => {
    setIsCustomStep(value);
  };

  const refetchStatusData = async () => {
    try {
      await refetchDomainStatusData();
    } catch (err) {
      console.error(err);
    }
  };

  if (domainStatusDataLoading)
    return (
      <div className="text-center my-5 py-5">
        <Spinner animation="border" role="status" />
      </div>
    );

  const commonUserDomainViewProps = {
    domainId: +projectId,
    domainStatusData,
    refetchStatusData,
    showCustomSecondStep,
    stepView,
    setStepView,
    urlType,
    isEditMode: true,
  };

  return (
    <>
      <h4 className="fs-3 fw-bold mb-3">{t("projects.Main Settings")}</h4>
      <FloatingLabel
        label={
          <>
            {t("projects.Project Title")} <span className="text-danger">*</span>
          </>
        }
      >
        <Form.Control
          required
          type="text"
          maxLength={255}
          placeholder={t("projects.Project Title")}
          onChange={(e) => setTitle(e.target.value)}
          value={title}
        />
      </FloatingLabel>
      <h4 className="fs-3 fw-bold mt-5 mb-2">{t("projects.Project Domain")}</h4>
      <div className="bg-primary-light p-3 rounded text-primary d-flex gap-3 mb-3 mb-md-4">
        <div>
          <InfoCircle className="fs-2" height={16} width={16} />
        </div>
        <p className="m-0">
          <span className="fw-bold d-block">
            {t("projects.What is a domain?")}
          </span>
          {t(
            "projects.A domain is your site’s address on the internet. You can use our domain to start instantly, or connect your own for a more branded experience."
          )}
        </p>
      </div>

      <div className="domainTypes d-flex flex-column align-items-start mb-4 fs-7 border border-light rounded-3 p-4">
        {options.map(({ value, label }) => (
          <Form.Label
            className={`${
              checkDomainTypeWithPremium(value) &&
              isDisableSubdomain &&
              "pe-none"
            }`}
            key={value}
          >
            <Form.Check
              type="radio"
              label={label}
              id={`domain-radio-${value}`}
              className="d-flex align-items-baseline"
              checked={selectedOption === value}
              onChange={() => setSelectedOption(value)}
            />
          </Form.Label>
        ))}
      </div>

      {selectedOption === DomainOptionType.Domain && (
        <div className="text-start fs-5">
          <span className="fw-bold">{t("projects.Your URL")}:</span>{" "}
          {window.location.origin}
          {lng}/projects/{projectId}
        </div>
      )}
      {selectedOption === DomainOptionType.Subdomain && (
        <Subdomain
          handleNextStepDisabled={handleNextStepDisabled}
          domainId={+projectId}
          domainStatusData={domainStatusData}
          refetchStatusData={refetchStatusData}
        />
      )}
      {selectedOption === DomainOptionType.UserDomain && (
        <UserDomainView {...commonUserDomainViewProps} />
      )}
      {selectedOption === DomainOptionType.UserSubdomain && (
        <UserDomainView {...commonUserDomainViewProps} isSubdomainMode />
      )}

      <Row className="pt-2 gy-3 justify-content-between mt-4 mt-md-5">
        <Col className="m-0">
          <Link to={`${lng}/projects`}>
            <Button
              variant="outline-dark"
              className="w-100 fw-normal dark-hover"
            >
              {t("projects.Cancel")}
            </Button>
          </Link>
        </Col>
        <Col className="m-0">
          <Button
            variant="primary"
            className="w-100 text-white fw-bold order-0 order-md-1"
            onClick={handleAddProject}
            disabled={loadingEditDomain || !title.trim().length}
          >
            <>
              {t("projects.Save")}
              {loadingEditDomain && (
                <Spinner
                  animation="border"
                  role="status"
                  size="sm"
                  className="ms-2"
                />
              )}
            </>
          </Button>
        </Col>
      </Row>
    </>
  );
}
